/**
 * pdf.js — translate a PDF in place, without rebuilding it.
 *
 * WHY NOT RASTERISE. The obvious implementation renders each page to an image,
 * paints the translation on it and makes a new PDF of pictures. That destroys
 * everything the file was: vectors go fuzzy, images are recompressed, the text
 * layer disappears (so the result cannot be searched, copied or read aloud),
 * and a 40 KB contract becomes a 6 MB scan of itself. Here the ORIGINAL page
 * objects are kept and the translation is drawn ON TOP: a filled rectangle in
 * the sampled paper colour over the old words, new text above it. Everything
 * that was not text is untouched because it is never re-encoded.
 *
 * WHY PARAGRAPHS, NOT LINES. A PDF stores a sentence as a row of positioned
 * fragments, and a wrapped sentence as several rows of them. Translating each
 * row is how the well-known tools produce word salad — the reviews all say
 * "breaks tables and columns", and this is the mechanism. Lines are merged back
 * into paragraphs first, translated whole, then re-wrapped into the space the
 * paragraph occupied.
 *
 * SCANNED PAGES ARE NOT AN ERROR. Prod telemetry for /pdf-to-word over 30 days:
 * 6 of its 8 errors are literally "В этом PDF нет текста. Сканы-картинки
 * требуют OCR перед извлечением", and 7 of its 10 abandons sit on the error
 * stage — the tool tells people to go find OCR elsewhere. A page with no text
 * layer is routed to the neural OCR engine instead, per PAGE, so a contract
 * with one scanned signature page still translates end to end.
 *
 * Exposed as window.DTPdf.
 */
(function (root) {
    'use strict';
    if (root.DTPdf) return;

    /* A paragraph is a stack of lines that share a left edge and sit one
       line-height apart. Both tolerances are fractions of the line height, so
       they hold at any font size and any page scale. */
    var LINE_GAP_MAX = 1.9;    // vertical gap / line height still "same paragraph"
    var LEFT_TOL = 0.9;        // left-edge drift / line height still "same paragraph"
    var SAME_LINE = 0.6;       // vertical centre distance / line height = same line
    /**
     * Horizontal gap / line height that means "different column", not "space".
     *
     * Without this, two columns at the same height merge into one line and then
     * into one paragraph — which is EXACTLY the failure every review names in
     * the well-known tools ("multi-column layouts get flattened"). A word space
     * runs 0.2–0.5 line heights; a gutter runs several. Measured on the test
     * case: a two-column page with a 10-line-height gutter merged silently
     * until this cut existed.
     */
    var GUTTER = 2.5;
    /** Line-height difference that means "different kind of text", not "same
     *  paragraph". 25% separates a heading from body copy and still tolerates
     *  the wobble a line with no descenders produces. */
    var SIZE_TOL = 0.25;

    function mean(a) { return a.length ? a.reduce(function (x, y) { return x + y; }, 0) / a.length : 0; }

    // ── page text → boxes ────────────────────────────────────────────────

    /**
     * pdf.js gives every fragment a transform in PDF user space. Convert to a
     * device-space box at `scale`, matching what the rendered canvas shows —
     * the same space the colour sampling below reads from.
     */
    function itemBox(Util, viewport, it) {
        var tx = Util.transform(viewport.transform, it.transform);
        var sx = Math.hypot(tx[0], tx[1]);
        var h = Math.hypot(tx[2], tx[3]) || 1;
        var w = (it.width || 0) * viewport.scale;
        if (!(w > 0) || !(h > 0) || !(sx > 0)) return null;

        // The bounding box of the actual run, not of a run assumed horizontal.
        // `{x, y - h, w, h}` is the recipe everyone copies and it is only right
        // when the text runs left-to-right in device space; on a page whose
        // content is sideways it reports a box stretching off the right edge —
        // measured on a /Rotate 90 page, where "ДОГОВОР № 12" came back at
        // x = 780..934 on an 842-wide sheet.
        var ux = tx[0] / sx, uy = tx[1] / sx;          // along the baseline
        var vx = tx[2] / h, vy = tx[3] / h;            // text "up", in device space
        var xs = [], ys = [];
        for (var a = 0; a <= 1; a++) {
            for (var b = 0; b <= 1; b++) {
                xs.push(tx[4] + ux * w * a + vx * h * b);
                ys.push(tx[5] + uy * w * a + vy * h * b);
            }
        }
        return {
            x: Math.min.apply(null, xs), y: Math.min.apply(null, ys),
            w: Math.max.apply(null, xs) - Math.min.apply(null, xs),
            h: Math.max.apply(null, ys) - Math.min.apply(null, ys),
            angle: Math.atan2(uy, ux),
        };
    }

    /**
     * Choose the viewport in which this page's text is UPRIGHT.
     *
     * `/Rotate` says how the page is turned for display, and for a sideways
     * scan it is exactly the correction needed. But it is metadata: a page can
     * carry the wrong value, carry none while its content is sideways, or carry
     * one that a previous tool already baked into the content. Trusting it means
     * the line grouper reads a column of vertical glyphs as one enormous line
     * and the whole document comes out as confetti — silently, because nothing
     * errors.
     *
     * So the page is ASKED instead of assumed: each of the four right-angle
     * viewports is scored by how much of the text runs left-to-right in it, and
     * the winner is used for everything downstream — sampling, grouping, and the
     * matrix the translation is drawn through. One measurement replaces a class
     * of special cases.
     */
    function uprightViewport(Util, page, items, scale) {
        var base = page.rotate || 0;
        var best = null;
        for (var k = 0; k < 4; k++) {
            var rot = ((base + k * 90) % 360 + 360) % 360;
            var vp = page.getViewport({ scale: scale, rotation: rot });
            var score = 0;
            for (var i = 0; i < items.length; i++) {
                var tx = Util.transform(vp.transform, items[i].transform);
                var sx = Math.hypot(tx[0], tx[1]);
                if (!(sx > 0)) continue;
                var weight = (items[i].width || 0) + 1;
                // cos of the baseline angle: 1 upright, 0 sideways, −1 upside down.
                score += (tx[0] / sx) * weight;
            }
            if (!best || score > best.score) best = { rot: rot, score: score, vp: vp };
        }
        return best || { rot: base, vp: page.getViewport({ scale: scale }) };
    }

    /** Fragments on one visual line, left to right. */
    function groupLines(items) {
        var sorted = items.slice().sort(function (a, b) {
            var ca = a.box.y + a.box.h / 2, cb = b.box.y + b.box.h / 2;
            var d = ca - cb;
            return Math.abs(d) > Math.max(a.box.h, b.box.h) * SAME_LINE ? d : a.box.x - b.box.x;
        });
        var lines = [], cur = null;
        sorted.forEach(function (it) {
            var c = it.box.y + it.box.h / 2;
            if (cur && Math.abs(c - cur.centre) <= Math.max(it.box.h, cur.h) * SAME_LINE) {
                cur.items.push(it);
                cur.h = Math.max(cur.h, it.box.h);
                cur.centre = mean(cur.items.map(function (q) { return q.box.y + q.box.h / 2; }));
            } else {
                cur = { items: [it], h: it.box.h, centre: c };
                lines.push(cur);
            }
        });
        var out = [];
        lines.forEach(function (l) {
            var its = l.items.sort(function (a, b) { return a.box.x - b.box.x; });

            // Split the visual line at column gutters BEFORE building text, so a
            // two-column page yields two lines rather than one merged sentence.
            var segments = [[its[0]]];
            for (var i = 1; i < its.length; i++) {
                var prev = its[i - 1].box;
                var gap = its[i].box.x - (prev.x + prev.w);
                if (gap > l.h * GUTTER) segments.push([its[i]]);
                else segments[segments.length - 1].push(its[i]);
            }

            var boldWeight = 0, plainWeight = 0;
            its.forEach(function (q) {
                var wgt = (q.box.w || 1);
                if (q.bold) boldWeight += wgt; else plainWeight += wgt;
            });
            var lineBold = boldWeight > plainWeight;

            segments.forEach(function (seg) {
                var x0 = Math.min.apply(null, seg.map(function (q) { return q.box.x; }));
                var y0 = Math.min.apply(null, seg.map(function (q) { return q.box.y; }));
                var x1 = Math.max.apply(null, seg.map(function (q) { return q.box.x + q.box.w; }));
                var y1 = Math.max.apply(null, seg.map(function (q) { return q.box.y + q.box.h; }));
                // Fragments abut without spaces in many producers; insert one
                // where the gap is wide enough to be a word break.
                var text = '';
                seg.forEach(function (q, i) {
                    if (i) {
                        var p = seg[i - 1].box;
                        var g = q.box.x - (p.x + p.w);
                        if (g > l.h * 0.18 && !/\s$/.test(text) && !/^\s/.test(q.text)) text += ' ';
                    }
                    text += q.text;
                });
                out.push({ text: text, x: x0, y: y0, w: x1 - x0, h: y1 - y0, lineH: l.h, bold: lineBold });
            });
        });
        // Reading order: down the page, then left to right. Column separation is
        // preserved by the paragraph grouper's left-edge test.
        return out.sort(function (a, b) { return a.y - b.y || a.x - b.x; });
    }

    /**
     * Lines → columns → paragraphs.
     *
     * The column pass is not decoration. Walking lines in plain top-to-bottom
     * order on a two-column page alternates between columns, so every line
     * starts a new paragraph and each column's sentences arrive at the
     * translator in pieces — the same flattening the gutter split above was
     * meant to prevent, one level up. Lines are clustered by horizontal overlap
     * first; a line that spans several clusters (a full-width heading or rule)
     * stays on its own so it is not dragged into either column.
     */
    function groupColumns(lines) {
        var cols = [];
        lines.slice().sort(function (a, b) { return a.x - b.x; }).forEach(function (ln) {
            var hit = null;
            for (var i = 0; i < cols.length; i++) {
                var c = cols[i];
                var overlap = Math.min(c.x1, ln.x + ln.w) - Math.max(c.x0, ln.x);
                if (overlap > Math.min(c.x1 - c.x0, ln.w) * 0.5) { hit = c; break; }
            }
            if (hit) {
                hit.x0 = Math.min(hit.x0, ln.x);
                hit.x1 = Math.max(hit.x1, ln.x + ln.w);
                hit.lines.push(ln);
            } else {
                cols.push({ x0: ln.x, x1: ln.x + ln.w, lines: [ln] });
            }
        });

        // A line wide enough to cover most of the page belongs to no column.
        var pageX0 = Math.min.apply(null, cols.map(function (c) { return c.x0; }));
        var pageX1 = Math.max.apply(null, cols.map(function (c) { return c.x1; }));
        var pageW = Math.max(1, pageX1 - pageX0);
        var full = [];
        cols.forEach(function (c) {
            c.lines = c.lines.filter(function (ln) {
                if (cols.length > 1 && ln.w > pageW * 0.8) { full.push(ln); return false; }
                return true;
            });
        });

        var out = cols.filter(function (c) { return c.lines.length; });
        if (full.length) out.push({ x0: pageX0, x1: pageX1, lines: full, fullWidth: true });
        // Reading order: full-width blocks by their own y, columns left to right.
        return out.sort(function (a, b) { return a.x0 - b.x0; });
    }

    /** Lines → paragraphs. A wrapped sentence must reach the translator whole. */
    function groupParagraphsInColumn(lines) {
        var paras = [], cur = null;
        lines.slice().sort(function (a, b) { return a.y - b.y; }).forEach(function (ln) {
            if (cur) {
                var gap = ln.y - (cur.y + cur.h);
                var leftDrift = Math.abs(ln.x - cur.x);
                // A change of type size is a paragraph boundary. Without this
                // a heading swallows the line under it, and the whole thing is
                // then re-set at the HEADING's size — measured on
                // contract-ru.pdf, where "ДОГОВОР № 12" absorbed the date line
                // and re-typeset it at 20 pt. Nothing in the geometry tests
                // catches it; only the rendered page shows it.
                var sizeJump = Math.abs(ln.lineH - cur.lineH)
                    > Math.max(ln.lineH, cur.lineH) * SIZE_TOL;
                var sameBlock = !sizeJump
                    && gap >= -cur.lineH * 0.5
                    && gap <= cur.lineH * LINE_GAP_MAX
                    && leftDrift <= cur.lineH * LEFT_TOL;
                if (sameBlock) {
                    // Hyphenated break: "прод-\nолжение" is one word, not two.
                    if (/[‐-―-]$/.test(cur.text)) cur.text = cur.text.replace(/[‐-―-]$/, '') + ln.text;
                    else cur.text += ' ' + ln.text;
                    cur.w = Math.max(cur.x + cur.w, ln.x + ln.w) - Math.min(cur.x, ln.x);
                    cur.x = Math.min(cur.x, ln.x);
                    cur.h = (ln.y + ln.h) - cur.y;
                    cur.lines++;
                    cur.leading = (ln.y - cur.y) / (cur.lines - 1);
                    if (!ln.bold) cur.bold = false;
                    return;
                }
            }
            cur = { text: ln.text, x: ln.x, y: ln.y, w: ln.w, h: ln.h, lineH: ln.lineH, lines: 1, leading: ln.lineH * 1.2, bold: !!ln.bold };
            paras.push(cur);
        });
        return paras;
    }

    /** Public grouper: columns first, then paragraphs inside each. */
    function groupParagraphs(lines) {
        if (!lines.length) return [];
        var out = [];
        groupColumns(lines).forEach(function (col) {
            out = out.concat(groupParagraphsInColumn(col.lines));
        });
        // Within a column the order is already top-down; across columns keep the
        // left-to-right order groupColumns established, so a reader following the
        // translated file reads it the way the page reads.
        return out;
    }

    // ── colour under a box ───────────────────────────────────────────────

    /**
     * Read the paper colour from a ring just outside the block, as a MEDIAN.
     * A mean is dragged by a neighbouring rule or a table border and the fill
     * then shows as a grey slab on white paper.
     */
    function paperUnder(ctx, box) {
        var pad = Math.max(2, Math.round(box.h * 0.25));
        var cw = ctx.canvas.width, ch = ctx.canvas.height;
        var x0 = Math.max(0, Math.floor(box.x - pad)), y0 = Math.max(0, Math.floor(box.y - pad));
        var x1 = Math.min(cw, Math.ceil(box.x + box.w + pad)), y1 = Math.min(ch, Math.ceil(box.y + box.h + pad));
        if (x1 <= x0 || y1 <= y0) return [255, 255, 255];
        var d = ctx.getImageData(x0, y0, x1 - x0, y1 - y0).data;
        var W = x1 - x0, H = y1 - y0;
        var inX0 = box.x - x0, inY0 = box.y - y0, inX1 = inX0 + box.w, inY1 = inY0 + box.h;
        var ch0 = [], ch1 = [], ch2 = [];
        for (var y = 0; y < H; y++) {
            for (var x = 0; x < W; x++) {
                if (x >= inX0 && x < inX1 && y >= inY0 && y < inY1) continue;
                var i = (y * W + x) * 4;
                ch0.push(d[i]); ch1.push(d[i + 1]); ch2.push(d[i + 2]);
            }
        }
        if (!ch0.length) return [255, 255, 255];
        var med = function (a) { a.sort(function (p, q) { return p - q; }); return a[a.length >> 1]; };
        return [med(ch0), med(ch1), med(ch2)];
    }

    /** Ink colour: the pixels inside the box furthest from the paper. */
    function inkIn(ctx, box, paper) {
        var cw = ctx.canvas.width, ch = ctx.canvas.height;
        var x0 = Math.max(0, Math.floor(box.x)), y0 = Math.max(0, Math.floor(box.y));
        var x1 = Math.min(cw, Math.ceil(box.x + box.w)), y1 = Math.min(ch, Math.ceil(box.y + box.h));
        if (x1 <= x0 || y1 <= y0) return [0, 0, 0];
        var d = ctx.getImageData(x0, y0, x1 - x0, y1 - y0).data;
        var lum = function (r, g, b) { return 0.2126 * r + 0.7152 * g + 0.0722 * b; };
        var pl = lum(paper[0], paper[1], paper[2]);
        var best = [], i;
        for (i = 0; i < d.length; i += 4) {
            best.push({ d: Math.abs(lum(d[i], d[i + 1], d[i + 2]) - pl), c: [d[i], d[i + 1], d[i + 2]] });
        }
        best.sort(function (a, b) { return b.d - a.d; });
        var take = Math.max(1, Math.round(best.length * 0.12));
        var acc = [0, 0, 0];
        for (i = 0; i < take; i++) { acc[0] += best[i].c[0]; acc[1] += best[i].c[1]; acc[2] += best[i].c[2]; }
        return [acc[0] / take, acc[1] / take, acc[2] / take];
    }


    // ── the page as the reader sees it ───────────────────────────────────

    /**
     * ONE matrix instead of four special cases.
     *
     * A PDF page can be rotated (`/Rotate 90`), can have a CropBox whose origin
     * is not (0,0), and always has y growing UP — while every layout decision
     * above is made in screen coordinates with y growing DOWN. Handling those
     * three by hand means three chances to be subtly wrong on somebody's file,
     * and the wrongness stays invisible until a real rotated document arrives.
     *
     * Instead the drawing is done in the coordinate space the READER sees — the
     * rendered page, y up from its bottom-left corner — and a single `cm`
     * operator carries that space into the file's own. pdf.js already computes
     * the forward transform for rendering; its inverse, composed with a y flip,
     * is exactly the matrix needed. Rotation, CropBox offset and scale then cost
     * nothing, because none of them is ever named again.
     */
    function localToUser(Util, vp) {
        var inv = Util.inverseTransform(vp.transform);      // device → user
        return Util.transform(inv, [1, 0, 0, -1, 0, vp.height]);
    }

    /**
     * Scale used ONLY to sample colour, never to produce output.
     *
     * Output is drawn as vectors into the original file, so this number cannot
     * affect sharpness — it only decides how well paper and ink colours are
     * read. 1.0 keeps rasterising under a second a page while still giving
     * `inkIn` real glyph pixels to look at; at 0.4 strokes blend into the paper
     * and every ink colour comes back washed toward grey.
     */
    var SAMPLE_SCALE = 1.0;

    function makeCanvas(w, h) {
        if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
        var c = document.createElement('canvas');
        c.width = w; c.height = h;
        return c;
    }

    async function renderPage(page, scale, rotation) {
        var vp = page.getViewport(rotation === undefined
            ? { scale: scale } : { scale: scale, rotation: rotation });
        var canvas = makeCanvas(Math.max(1, Math.ceil(vp.width)), Math.max(1, Math.ceil(vp.height)));
        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        // Paper first: a page that paints no background is transparent, and
        // sampling transparent pixels returns (0,0,0,0) — i.e. black paper.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvasContext: ctx, viewport: vp }).promise;
        return { canvas: canvas, ctx: ctx, viewport: vp };
    }

    /**
     * Is this run bold? pdf.js hands out an internal font id (`g_d0_f1`), so the
     * real name has to be asked for. `commonObjs` holds it once the page has
     * been rendered or its text read; when it does not, the style table's family
     * string is the fallback. Neither is authoritative, which is why bold only
     * ever picks a face variant and never decides layout.
     */
    function isBold(page, styles, item) {
        var names = [String(item.fontName || '')];
        try {
            if (page.commonObjs && page.commonObjs.has(item.fontName)) {
                var f = page.commonObjs.get(item.fontName);
                if (f && f.name) names.push(String(f.name));
                if (f && f.bold === true) return true;
            }
        } catch (e) { /* not resolved yet — fall through to the style table */ }
        var st = styles && styles[item.fontName];
        if (st && st.fontFamily) names.push(String(st.fontFamily));
        return /bold|black|heavy|semib|demib/i.test(names.join(' '));
    }

    /**
     * How much empty paper sits under each paragraph, and how it is aligned.
     *
     * A translation is routinely 15–25% longer than its source (EN→RU, EN→DE),
     * so "wrap it into the original box" either overflows onto the next
     * paragraph or forces a shrink that makes the page look retyped. The gap to
     * whatever comes next is free room, and knowing it is the difference between
     * a page that still looks like itself and one that does not.
     */
    function addSpaceBelow(paras, pageH, pageW) {
        paras.forEach(function (p) {
            var bottom = p.y + p.h;
            var limit = pageH - bottom;
            paras.forEach(function (q) {
                if (q === p) return;
                var overlap = Math.min(p.x + p.w, q.x + q.w) - Math.max(p.x, q.x);
                if (overlap <= 0) return;                    // different column
                if (q.y < bottom - p.lineH * 0.3) return;    // above us
                limit = Math.min(limit, q.y - bottom);
            });
            p.spaceBelow = Math.max(0, limit);

            // Room to the RIGHT, by the same rule. A heading may grow into the
            // empty margin beside it; a table cell has a neighbour there and
            // must not, or the translated table overlaps its own next column.
            var right = pageW - (p.x + p.w);
            paras.forEach(function (q) {
                if (q === p) return;
                var vOverlap = Math.min(p.y + p.h, q.y + q.h) - Math.max(p.y, q.y);
                if (vOverlap <= 0) return;
                if (q.x < p.x + p.w) return;
                right = Math.min(right, q.x - (p.x + p.w));
            });
            p.spaceRight = Math.max(0, right);
        });

        // Alignment, read from the block itself: a paragraph inset equally on
        // both sides of the text body was centred on the page, and re-wrapping
        // it flush left is the single most obvious "this was machine-translated"
        // tell on a title page.
        if (!paras.length) return;
        var colX0 = Math.min.apply(null, paras.map(function (q) { return q.x; }));
        var colX1 = Math.max.apply(null, paras.map(function (q) { return q.x + q.w; }));
        paras.forEach(function (p) {
            var leftGap = p.x - colX0, rightGap = colX1 - (p.x + p.w);
            var tol = p.lineH * 0.8;
            if (leftGap > tol && Math.abs(leftGap - rightGap) < tol) p.align = 'center';
            else if (leftGap > tol && rightGap < tol * 0.5) p.align = 'right';
            else p.align = 'left';
        });
    }

    // ── reading a document ───────────────────────────────────────────────

    /**
     * open(file, deps) → { kind, units, meta, build(translated, opts) }
     *
     * deps = { pdfjsLib, PDFLib, PdfTextFont, ocrPage?, onProgress? }
     *
     * `ocrPage(canvas, pageIndex)` is optional and is what makes a scanned
     * contract translatable: it returns [{ text, box:{x,y,w,h} }] in the same
     * canvas pixels this module already works in, so an OCR'd page and a real
     * text layer travel an identical path from here on.
     */
    async function open(file, deps) {
        deps = deps || {};
        var pdfjsLib = deps.pdfjsLib;
        if (!pdfjsLib) throw new Error('pdfjs_missing');
        var Util = pdfjsLib.Util;
        var onProgress = deps.onProgress || function () {};

        var original = (file instanceof Uint8Array) ? file : new Uint8Array(await file.arrayBuffer());
        // pdf.js TRANSFERS the array it is handed to its worker. Give it a copy:
        // sharing one buffer leaves `original` detached and zero-length, and the
        // failure surfaces much later, as pdf-lib refusing a "corrupt" file.
        var doc = await pdfjsLib.getDocument({
            data: original.slice(),
            isEvalSupported: false,
            useSystemFonts: false,
        }).promise;

        var units = [];
        var pageInfo = [];
        var scanned = [];
        var totalSkewed = 0;

        for (var pi = 1; pi <= doc.numPages; pi++) {
            var page = await doc.getPage(pi);
            var content = await page.getTextContent();
            var real = content.items.filter(function (q) { return q.str && q.str.trim() && q.transform; });
            var upright = uprightViewport(Util, page, real, SAMPLE_SCALE);
            var vp0 = upright.vp;

            var items = [], skewed = 0;
            for (var k = 0; k < real.length; k++) {
                var it = real[k];
                var box = itemBox(Util, vp0, it);
                if (!box) continue;
                // A run still at an angle after the page has been squared up is
                // a sideways label or a watermark. Re-wrapping it as if it were
                // body text turns it into a smear across the page, so it is left
                // alone — and counted, so the tool can say so.
                if (Math.abs(box.angle) > 0.35) { skewed++; continue; }
                items.push({ text: it.str, box: box, bold: isBold(page, content.styles, it) });
            }
            totalSkewed += skewed;

            var isScan = !items.some(function (q) { return /\p{L}/u.test(q.text); });
            var rendered = await renderPage(page, SAMPLE_SCALE, upright.rot);

            if (isScan && deps.ocrPage) {
                var found = await deps.ocrPage(rendered.canvas, pi - 1);
                items = (found || [])
                    .filter(function (q) { return q.text && q.text.trim(); })
                    .map(function (q) { return { text: q.text, box: q.box, bold: false }; });
                if (items.length) scanned.push(pi - 1);
            } else if (isScan) {
                scanned.push(pi - 1);
            }

            var paras = groupParagraphs(groupLines(items));
            addSpaceBelow(paras, vp0.height, vp0.width);

            paras.forEach(function (para) {
                if (!/\p{L}/u.test(para.text)) return;      // rules, page numbers, dingbats
                var pad = Math.max(1, para.lineH * 0.12);
                var paper = paperUnder(rendered.ctx, {
                    x: para.x - pad, y: para.y - pad,
                    w: para.w + pad * 2, h: para.h + pad * 2,
                });
                units.push({
                    id: units.length,
                    page: pi - 1,
                    text: para.text,
                    // Reader space at SAMPLE_SCALE, y still measured DOWN here;
                    // the flip happens once, inside the matrix.
                    box: { x: para.x, y: para.y, w: para.w, h: para.h },
                    // The measured font size, not a fraction of it: pdf.js's
                    // item transform carries the exact size, and scaling it
                    // "to be safe" retypesets the whole document a little
                    // smaller than it was.
                    size: para.lineH,
                    leading: para.leading,
                    lines: para.lines,
                    spaceBelow: para.spaceBelow,
                    spaceRight: para.spaceRight,
                    align: para.align,
                    bold: !!para.bold,
                    paper: paper,
                    ink: inkIn(rendered.ctx, para, paper),
                    source: isScan ? 'ocr' : 'text',
                });
            });

            pageInfo.push({
                index: pi - 1,
                matrix: localToUser(Util, vp0),
                // user → device, needed by the eraser to find the words it must
                // delete from the content stream.
                transform: vp0.transform.slice(),
                width: vp0.width,
                height: vp0.height,
            });
            // Release the bitmap before the next page: a 300-page document at
            // A4 and scale 1.0 is ~180 MB of canvas if they all stay alive.
            rendered.canvas.width = rendered.canvas.height = 1;
            page.cleanup();
            onProgress({ stage: 'read', page: pi, pages: doc.numPages });
        }

        await doc.destroy();

        return {
            kind: 'pdf',
            units: units.map(function (u) { return { id: u.id, text: u.text }; }),
            meta: {
                pages: pageInfo.length,
                paragraphs: units.length,
                chars: units.reduce(function (n, u) { return n + u.text.length; }, 0),
                // Honest disclosure material, the same role docx's `flattened`
                // plays: pages that had no text layer at all.
                scanned: scanned,
                ocr: units.filter(function (u) { return u.source === 'ocr'; }).length,
                // Runs left at an angle on purpose: sideways labels, watermarks.
                skewed: totalSkewed,
            },
            build: function (translated, opts) {
                return buildPdf(original, units, pageInfo, translated, opts || {}, deps);
            },
        };
    }


    // ── writing the translation back ─────────────────────────────────────

    /**
     * Words, not characters — but characters when a word is wider than the
     * column. A URL or a German compound can be longer than the box it has to
     * live in, and a wrapper that only breaks at spaces silently lets it run off
     * the page edge.
     */
    function atomize(TF, text, prefer) {
        var atoms = [];
        TF.planRuns(text, prefer).forEach(function (r) {
            var parts = String(r.text).split(/(\s+)/);
            for (var i = 0; i < parts.length; i++) {
                if (!parts[i]) continue;
                atoms.push({ text: parts[i], faceId: r.faceId, space: /^\s+$/.test(parts[i]) });
            }
        });
        return atoms;
    }

    /* A word is measured ONCE per face, at 1000 units, and scaled: pdf-lib's
       width is exactly proportional to the size. ⚠️ Until 23.10.2026 every
       measurement ran fontkit's full OpenType layout (GSUB/GPOS lookups), and
       fitParagraph re-wraps a paragraph up to 14 times at shrinking sizes — the
       same words, measured again each time. Profile of a 400-page build: ~85 %
       of an 11.3-second uninterrupted task was fontkit layout; at 12–18
       thousand paragraphs a weaker laptop froze long enough for Chrome to offer
       «Page unresponsive», and the crash reports of this tool show exactly that
       (12 `unresponsive` kills 20.09–22.10, documents of 8–18k paragraphs). */
    var _widths = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
    function widthOf(font, text, size) {
        var m = _widths ? _widths.get(font) : null;
        if (_widths && !m) { m = new Map(); _widths.set(font, m); }
        var w = m ? m.get(text) : undefined;
        if (w === undefined) {
            try { w = font.widthOfTextAtSize(text, 1000) / 1000; }
            catch (e) { w = 0.55 * text.length; }      // uncoverable glyph — estimate
            if (m) m.set(text, w);
        }
        return w * size;
    }

    /* Give the main thread back every ~30 ms of building work: a click, a
       repaint, the browser's own hang watchdog all wait otherwise. The shared
       door (__yieldTick, timbrica-core.js) uses a message channel, so a hidden
       tab is not slowed to one step a second. */
    function nowMs() { return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now(); }
    function breather() {
        var last = nowMs();
        return async function () {
            if (nowMs() - last < 30) return;
            if (root.__yieldTick) await root.__yieldTick(0);
            else await new Promise(function (r) { setTimeout(r, 0); });
            last = nowMs();
        };
    }

    function wrapAtoms(atoms, fontFor, size, maxW) {
        var lines = [], cur = [], w = 0;

        function push(atom, aw) { cur.push({ atom: atom, w: aw }); w += aw; }
        function flush() { if (cur.length) { lines.push({ parts: cur, w: w }); cur = []; w = 0; } }

        atoms.forEach(function (a) {
            var font = fontFor(a.faceId);
            var aw = widthOf(font, a.text, size);

            if (a.space) {
                if (cur.length) push(a, aw);                 // never start a line with a space
                return;
            }
            if (cur.length && w + aw > maxW) flush();

            if (aw > maxW && a.text.length > 1) {
                // Hard break: fill the line character by character. Rare, and
                // the alternative is text outside the page.
                var chunk = '';
                for (var i = 0; i < a.text.length; i++) {
                    var next = chunk + a.text[i];
                    if (chunk && widthOf(font, next, size) + w > maxW) {
                        push({ text: chunk, faceId: a.faceId }, widthOf(font, chunk, size));
                        flush();
                        chunk = a.text[i];
                    } else {
                        chunk = next;
                    }
                }
                if (chunk) push({ text: chunk, faceId: a.faceId }, widthOf(font, chunk, size));
                return;
            }
            push(a, aw);
        });
        flush();
        // Trailing spaces must not count toward a centred line's width.
        lines.forEach(function (l) {
            while (l.parts.length && l.parts[l.parts.length - 1].atom.space) {
                l.w -= l.parts.pop().w;
            }
        });
        return lines;
    }

    /**
     * Fit the translation into the room the original paragraph had.
     *
     * The order of concessions is deliberate and is where the well-known tools
     * give up: they either overflow (text lands on the next paragraph) or shrink
     * immediately (the page visibly changes typeface size). Here the free space
     * BELOW the block is spent first — it costs nothing, because nothing is
     * there — and only a block with no room left is scaled, never below 0.62 of
     * the original or the page starts to look retyped.
     */
    function fitParagraph(atoms, fontFor, unit, maxW, avail) {
        var size = unit.size;
        var ratio = unit.lines > 1 && unit.leading ? unit.leading / unit.size : 1.22;
        var best = null;
        for (var attempt = 0; attempt < 14; attempt++) {
            var lines = wrapAtoms(atoms, fontFor, size, maxW);
            var leading = size * ratio;
            var need = (lines.length - 1) * leading + size;
            best = { lines: lines, size: size, leading: leading, need: need };
            if (need <= avail || size <= unit.size * 0.62) break;
            size *= 0.94;
        }
        return best;
    }

    /**
     * What the shipped faces cannot draw is settled per paragraph BEFORE layout.
     *
     * PdfTextFont returns such characters as a run with `faceId === null` and
     * leaves the decision to the caller. Until 09.10.2026 this caller made none:
     * one ☆ or ① anywhere in a 280 000-character manual left `pick(allText)`
     * null, `fontFor(null)` threw, and the visitor — after the whole translation
     * — read «Не удалось собрать документ». RU, 30 d: 305 sessions with
     * `no_face_for_target_language`, against 537 PDFs that did build; the
     * reported file (#8988, a Premium bought for this tool minutes earlier) had
     * nine kinds of such marks in 4 276 paragraphs: ①–④, ☆, ▘, fullwidth ）and：.
     *
     * The order of concessions: a compatibility form that we CAN draw (NFKC —
     * ①→1, （→(, ﬁ→fi, Ⅳ→IV); a decorative mark we cannot is left out and
     * counted; a LETTER we cannot draw is never dropped — that paragraph keeps
     * its original wording instead of losing a word silently. Only a document
     * where no paragraph can be written at all is the honest
     * `no_face_for_target_language` (a target script we ship no face for).
     */
    var MUST_DRAW = /[\p{L}\p{M}]/u;
    function settleGlyphs(TF, text, stats) {
        var runs = TF.planRuns(text, null);
        var gap = false;
        for (var i = 0; i < runs.length; i++) {
            if (runs[i].faceId === null && /\S/.test(runs[i].text)) { gap = true; break; }
        }
        if (!gap) return text;
        var out = '';
        for (var r = 0; r < runs.length; r++) {
            var run = runs[r];
            if (run.faceId !== null) { out += run.text; continue; }
            for (var k = 0; k < run.text.length; k++) {
                var cp = run.text.codePointAt(k);
                var ch = String.fromCodePoint(cp);
                if (cp > 0xffff) k++;
                if (/\s/.test(ch)) { out += ch; continue; }
                var nf = ch.normalize ? ch.normalize('NFKC') : ch;
                if (nf !== ch && TF.pick(nf)) { out += nf; stats.normalized++; continue; }
                if (MUST_DRAW.test(ch)) return null;
                stats.dropped++;
                stats.chars[ch] = (stats.chars[ch] || 0) + 1;
            }
        }
        return out;
    }

    /* The face that draws most of the text, for when no single face draws all
       of it (Russian next to Korean): `prefer` must never be null, because a
       leading space inherits it. */
    function dominantFace(TF, text) {
        var n = {}, best = null;
        TF.planRuns(text, null).forEach(function (r) {
            if (r.faceId) n[r.faceId] = (n[r.faceId] || 0) + r.text.length;
        });
        Object.keys(n).forEach(function (k) { if (!best || n[k] > n[best]) best = k; });
        return best;
    }

    async function buildPdf(original, units, pageInfo, translated, opts, deps) {
        var PDFLib = deps.PDFLib, TF = deps.PdfTextFont;
        if (!PDFLib) throw new Error('pdflib_missing');
        if (!TF) throw new Error('fonts_missing');

        var pdfDoc = await PDFLib.PDFDocument.load(original, {
            ignoreEncryption: true,
            updateMetadata: false,
        });
        var pages = pdfDoc.getPages();

        // Only faces that can actually render the translation get embedded, and
        // the CJK table is fetched only when the text needs it — it is ~8 MB of
        // coverage data that a Russian contract has no use for.
        await TF.load();
        var allText = units.map(function (u) { return translated[u.id] || ''; }).join('');
        if (TF.needsCjk(allText)) await TF.loadCjk();

        var breathe = breather();
        var glyphs = { normalized: 0, dropped: 0, kept: 0, chars: {} };
        var settled = {};
        for (var s = 0; s < units.length; s++) {
            await breathe();
            var su = units[s], st = translated[su.id];
            if (st == null || st === '' || st === su.text) continue;
            var fixed = settleGlyphs(TF, String(st), glyphs);
            if (fixed === null || !/\S/.test(fixed)) { glyphs.kept++; continue; }
            settled[su.id] = fixed;
        }
        var settledText = Object.keys(settled).map(function (id) { return settled[id]; }).join('');
        if (!settledText && glyphs.kept) throw new Error('no_face_for_target_language');

        var prefer = opts.faceId || TF.pick(settledText) || dominantFace(TF, settledText) || null;
        var fontCache = {};
        async function fontFor(faceId, bold) {
            var id = faceId || prefer;
            if (!id) throw new Error('no_face_for_target_language');
            var key = id + (bold ? '|b' : '');
            if (!fontCache[key]) {
                fontCache[key] = await TF.embed(pdfDoc, id, { subset: true, bold: !!bold });
            }
            return fontCache[key];
        }

        // Two passes: gather every face first so `fontFor` inside the layout is
        // synchronous and the drawing order stays exactly the reading order.
        var plans = [];
        for (var i = 0; i < units.length; i++) {
            await breathe();
            var u = units[i];
            var text = settled[u.id];
            if (text == null) continue;
            var atoms = atomize(TF, text, prefer);
            for (var a = 0; a < atoms.length; a++) await fontFor(atoms[a].faceId, u.bold);
            plans.push({ unit: u, atoms: atoms });
        }
        var sync = function (faceId, bold) {
            return fontCache[(faceId || prefer) + (bold ? '|b' : '')]
                || fontCache[faceId || prefer];
        };

        var byPage = {};
        plans.forEach(function (pl) {
            (byPage[pl.unit.page] = byPage[pl.unit.page] || []).push(pl);
        });

        var rgb = PDFLib.rgb;
        var shrunk = 0, overflowed = 0;
        var erased = { shown: 0, inside: 0, dropped: 0, chainKept: 0, forms: 0 };
        var seenForms = {};

        for (var pg = 0; pg < pageInfo.length; pg++) {
            await breathe();
            var info = pageInfo[pg];
            var list = byPage[info.index];
            if (!list || !list.length) continue;
            var page = pages[info.index];
            var M = info.matrix, H = info.height;

            // FIRST delete the original words, THEN draw. A rectangle only
            // hides text; the file still hands the original back to Ctrl+F,
            // to copy-paste and to every extractor — including ours. Order
            // matters: the eraser replaces the page's content stream, so
            // drawing before it would be wiped.
            var eraser = deps.PdfTextErase || root.PdfTextErase;
            if (eraser && !opts.keepSourceText) {
                try {
                    var st = eraser.erasePage(PDFLib, page,
                        list.map(function (pl) {
                            var b = pl.unit.box, pad = Math.max(0.75, pl.unit.size * 0.14);
                            return { x: b.x - pad, y: b.y - pad, w: b.w + pad * 2, h: b.h + pad * 2 };
                        }),
                        info.transform, { seenForms: seenForms });
                    Object.keys(st).forEach(function (k) {
                        if (erased[k] !== undefined) erased[k] += st[k];
                    });
                } catch (e) {
                    // A page we cannot rewrite still gets its translation drawn
                    // on top — visibly correct, with the old text underneath.
                    // Counted, never silent.
                    erased.failed = (erased.failed || 0) + 1;
                }
            }

            page.pushOperators(
                PDFLib.pushGraphicsState(),
                PDFLib.concatTransformationMatrix(M[0], M[1], M[2], M[3], M[4], M[5])
            );

            list.forEach(function (pl) {
                var u = pl.unit;
                var maxW = u.box.w + (u.lines > 1 ? 0 : Math.max(0, u.spaceRight || 0) * 0.9);
                var avail = u.box.h + Math.max(0, u.spaceBelow) * 0.85;
                var fontOf = function (fid) { return sync(fid, u.bold); };
                var fit = fitParagraph(pl.atoms, fontOf, u, maxW, avail);
                if (fit.size < u.size * 0.995) shrunk++;
                if (fit.need > avail + 0.5) overflowed++;

                // Erase: a hair of padding, because glyph antialiasing spills
                // half a pixel past the measured box and a bare rectangle leaves
                // a grey ghost of the old line along its top edge.
                var pad = Math.max(0.75, u.size * 0.14);
                var top = H - (u.box.y - pad);
                var boxH = u.box.h + pad * 2;
                var drawH = Math.max(boxH, fit.need + pad * 2);
                page.drawRectangle({
                    x: u.box.x - pad,
                    y: top - drawH,
                    width: maxW + pad * 2,
                    height: drawH,
                    color: rgb(u.paper[0] / 255, u.paper[1] / 255, u.paper[2] / 255),
                });

                var ink = rgb(
                    Math.min(1, Math.max(0, u.ink[0] / 255)),
                    Math.min(1, Math.max(0, u.ink[1] / 255)),
                    Math.min(1, Math.max(0, u.ink[2] / 255))
                );
                var ascent = fit.size * 0.78;
                try {
                    var f0 = fontOf(null);
                    if (f0 && f0.heightAtSize) ascent = f0.heightAtSize(fit.size, { descender: false });
                } catch (e) { /* keep the estimate */ }

                fit.lines.forEach(function (line, li) {
                    var x = u.box.x;
                    if (u.align === 'center') x = u.box.x + (maxW - line.w) / 2;
                    else if (u.align === 'right') x = u.box.x + maxW - line.w;
                    var baseline = (H - u.box.y) - ascent - li * fit.leading;
                    line.parts.forEach(function (part) {
                        if (part.atom.space) { x += part.w; return; }
                        page.drawText(part.atom.text, {
                            x: x, y: baseline, size: fit.size,
                            font: fontOf(part.atom.faceId), color: ink,
                        });
                        x += part.w;
                    });
                });
            });

            page.pushOperators(PDFLib.popGraphicsState());
        }

        // Side-by-side: the original page kept next to its translation. Asked
        // for constantly on legal and academic work, where the translation is a
        // reading aid and the source is the document of record.
        if (opts.bilingual) {
            var src = await PDFLib.PDFDocument.load(original, { ignoreEncryption: true });
            var copies = await pdfDoc.copyPages(src, src.getPageIndices());
            for (var c = copies.length - 1; c >= 0; c--) pdfDoc.insertPage(c + 1, copies[c]);
        }

        var out = await pdfDoc.save({ useObjectStreams: true });
        return {
            blob: new Blob([out], { type: 'application/pdf' }),
            stats: {
                drawn: plans.length, shrunk: shrunk, overflowed: overflowed,
                // `chainKept` is the honest count of runs that had to stay
                // because deleting them would have shifted neighbouring text.
                erased: erased,
                // What the faces could not draw: rewritten (NFKC), left out
                // (decorative marks) and paragraphs kept in the original wording.
                glyphs: {
                    normalized: glyphs.normalized, dropped: glyphs.dropped, kept: glyphs.kept,
                    chars: Object.keys(glyphs.chars).sort(function (a, b) {
                        return glyphs.chars[b] - glyphs.chars[a];
                    }).slice(0, 8).join(''),
                },
            },
        };
    }

    root.DTPdf = {
        _itemBox: itemBox,
        _groupLines: groupLines,
        _groupParagraphs: groupParagraphs,
        _groupColumns: groupColumns,
        _paperUnder: paperUnder,
        _inkIn: inkIn,
        _addSpaceBelow: addSpaceBelow,
        _localToUser: localToUser,
        _uprightViewport: uprightViewport,
        open: open,
        _settleGlyphs: settleGlyphs,
        _dominantFace: dominantFace,
        _wrapAtoms: wrapAtoms,
        _fitParagraph: fitParagraph,
        _atomize: atomize,
    };
})(typeof self !== 'undefined' ? self : this);
